/**
 * Image Hotspot Element Player
 * Image with clickable hotspots that reveal content in a popover/dialog
 */

import * as React from 'react';
import { cn } from '@ai-content-hub/content-kit';
import {
    ImageDropCanvas,
    useCanvasSize,
    getCanvasScale,
} from '@ai-content-hub/content-kit';
import { BlockRenderer } from '../block';
import {
    type ImageHotspotElementData,
    type ImageHotspotItem,
    normalizeImageHotspotData,
    getBackgroundImageSrc,
    getBackgroundImageAlt,
} from './schema';

const RADIUS_MIN = 20;
const RADIUS_MAX = 150;

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function InfoHotspotMarker({
    hotspot,
    onClick,
    className,
}: {
    hotspot: ImageHotspotItem;
    onClick: () => void;
    className?: string;
}) {
    const canvasSize = useCanvasSize();
    const radius = clamp(hotspot.radius, RADIUS_MIN, RADIUS_MAX);
    const baseDiameter = radius * 2;
    const diameter = baseDiameter * getCanvasScale(canvasSize);

    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            className={cn(
                'absolute flex items-center justify-center rounded-full border-2',
                'border-primary/80 bg-primary/30 hover:bg-primary/50',
                'cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                className
            )}
            style={{
                left: `${hotspot.x}%`,
                top: `${hotspot.y}%`,
                width: diameter,
                height: diameter,
                marginLeft: -diameter / 2,
                marginTop: -diameter / 2,
            }}
            title={hotspot.label}
            aria-label={hotspot.label || 'Hotspot'}
        >
            <span className="text-xs font-medium text-primary-foreground truncate px-1">
                {hotspot.label || '•'}
            </span>
        </button>
    );
}

export interface ImageHotspotPlayerProps {
    data?: ImageHotspotElementData;
    value?: ImageHotspotElementData;
    className?: string;
}

export const ImageHotspotPlayer: React.FC<ImageHotspotPlayerProps> = ({
    data: dataProp,
    value: valueProp,
    className,
}) => {
    const data = React.useMemo(
        () => normalizeImageHotspotData(dataProp ?? valueProp ?? {}),
        [dataProp, valueProp]
    );

    const [activeHotspot, setActiveHotspot] = React.useState<ImageHotspotItem | null>(null);

    const bgSrc = getBackgroundImageSrc(data.background);
    const bgAlt = getBackgroundImageAlt(data.background);

    if (!bgSrc?.trim()) {
        return (
            <div className={cn('text-muted-foreground italic p-4', className)}>
                No image
            </div>
        );
    }

    return (
        <div className={cn('image-hotspot-player space-y-4', className)}>
            <div className="relative">
                <ImageDropCanvas src={bgSrc} alt={bgAlt} className="w-full" fitViewport>
                    {data.hotspots.map((hotspot) => (
                        <InfoHotspotMarker
                            key={hotspot.id}
                            hotspot={hotspot}
                            onClick={() =>
                                setActiveHotspot((prev) =>
                                    prev?.id === hotspot.id ? null : hotspot
                                )
                            }
                        />
                    ))}
                </ImageDropCanvas>
            </div>

            {activeHotspot && (
                <div
                    className="rounded-lg border bg-muted/30 p-4 animate-in fade-in slide-in-from-top-2 duration-200"
                    role="region"
                    aria-label={`Content for ${activeHotspot.label}`}
                >
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <h4 className="font-semibold text-sm">
                            {activeHotspot.label || 'Hotspot'}
                        </h4>
                        <button
                            type="button"
                            onClick={() => setActiveHotspot(null)}
                            className="text-muted-foreground hover:text-foreground text-sm"
                        >
                            Close
                        </button>
                    </div>
                    <div className="prose prose-sm max-w-none">
                        <BlockRenderer content={activeHotspot.content} compact />
                    </div>
                </div>
            )}
        </div>
    );
};
